/**
 * MESSAGE CREW — built to the owner's own preview (25.9.2026, materials/Message-crew-preview.png), on his own
 * background and his own title; every number is measured off that picture, art/src/crew-popup-2026-09-25/README.md.
 *
 * Sending is a two-step now, on his instruction: "lyhyt vastaus valitaan ja lähetetään vasta send painikkeesta, ei
 * niin kuin nyt suoraan, parempi varmistaa lähetys SEND painikkeella. Jos valmis vastaus valitaan, niin teksti kenttä
 * tyhjenee, jos tekstikenttään kijoittaa, niin valmisvastauksen aktivointi poistuu eli vain joko vapaa viesti tai
 * valmis vastaus, ei molempia." So a quick line only arms itself, the field and the quick line clear each other, and
 * SEND is the one thing that sends. SEND is dark until there is something to send.
 *
 * A line goes to everyone in the finished match as a room event (`crew.say` → `crew.said`), shown as a toast; nothing
 * is stored. It creates no friendship, challenge or rematch (34-match-end.md §3).
 */
import { art } from '../art';
import { useState } from 'preact/hooks';
import { sheet, showToast } from '../state/store';
import { socket } from '../net/socket';
import { PopupPanel } from './PopupPanel';
import { sfx } from '../audio/sfx';

/** the popup's box on the stage: his background drawn 790 wide puts its neon at x 130 … 805, y 440 … 1235 */
const PLACE = { x: 72.14, y: 344.34, w: 790 };
/** 46-message-crew.md: the six quick lines — live text, localisable */
const QUICK = ['GG!', 'One more?', 'That was wild!', 'You read me!', 'Same time tomorrow?', 'NoWay!'];
/* Codex's measurement of the owner's preview (CODEX-SPEC.md): each chip's NEON CENTRE LINE, converted to this
   popup, then grown so the drawn piece's stroke lands on that line — pill.large carries 32 px of glow beside its
   stroke and 35 above it, of a 764 × 248 piece, so the box is bigger than the line by that much at this size. */
const CHIP = [
  /* the first row 20 up, and every pill 10 narrower with 5 added to its left (owner 25.9.2026) */
  { x: 69.30, y: 289.80, w: 221.93 }, { x: 283.75, y: 289.80, w: 223.95 }, { x: 500.22, y: 289.80, w: 221.93 },
  { x: 69.30, y: 408.26, w: 221.25 }, { x: 283.07, y: 408.26, w: 224.62 }, { x: 500.22, y: 408.26, w: 221.93 },
];

export function CrewSheet() {
  const [text, setText] = useState(''); const [pick, setPick] = useState<number | null>(null); const [busy, setBusy] = useState(false);
  const close = () => { sheet.value = null; };
  const chosen = pick !== null ? QUICK[pick]! : text.trim();
  const off = busy || !chosen;
  const tapChip = (i: number) => { sfx.tap(); setPick(i === pick ? null : i); setText(''); };
  const type = (v: string) => { setText(v); if (v) setPick(null); };
  const send = async () => {
    const t = chosen.trim().slice(0, 80); if (!t || busy) return; setBusy(true); sfx.tap();
    const a = await socket.send({ type: 'crew.say', text: t }); setBusy(false);
    if (!a.ok) { showToast(a.message); return; }
    setText(''); setPick(null); close();
  };
  const plates = {
    '--chip': `url(${art('/art/lobby-kit/pill.large.png')})`,
    '--cta': `url(${art('/art/lobby-kit/cta.plate.png')})`,
  } as Record<string, string>;

  return (
    <div class="sheet-backdrop" onClick={close} data-testid="crew-sheet">
      <PopupPanel title="MESSAGE CREW" titleArt="/art/popup/crew.title.png" label="Message crew" onClose={close}
        closeTestid="crew-close" plate="crew" place={PLACE} palms={false} rule={false} head={false} cls="mc-panel">
        <div class="mc-chips" style={plates}>
          {QUICK.map((q, i) => { const b = CHIP[i]!; return (
            <button key={q} class={`mc-chip${pick === i ? ' on' : ''}`} type="button" data-testid={`crew-quick-${i + 1}`}
              style={{ left: b.x, top: b.y, width: b.w }} aria-pressed={pick === i} aria-label={q} disabled={busy}
              onClick={() => tapChip(i)}>{q}</button>); })}
        </div>
        <div class="mc-field" style={plates}>
          <img class="mc-mail" src={art('/art/ui4b/icon-envelope@2x.png')} alt="" draggable={false} />
          <input value={text} maxLength={80} placeholder="Say something to crew" data-testid="crew-input" aria-label="Message to the crew"
            onInput={(e) => type((e.target as HTMLInputElement).value)} onKeyDown={(e) => { if (e.key === 'Enter' && !off) void send(); }} />
        </div>
        <button class={`mc-send${off ? ' off' : ''}`} type="button" style={plates} data-testid="crew-send" aria-label="Send"
          disabled={off} onClick={() => void send()}>SEND</button>
        <p class="mc-note">{busy ? 'Sending…' : 'Everyone in this match sees it'}</p>
      </PopupPanel>
    </div>
  );
}
